import { MultiSelect } from '@mantine/core';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';

const debouncedLocations = AwesomeDebouncePromise((fetch, url, query) => {
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify({ locations: { locationNames: { names: [query] } } }),
  });
}, 300);

const GoogleLocationSelector = ({ selected, handleSelect }) => {
  const fetch = useFetch();
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedToShow, setSelectedToShow] = useState([]);
  const [error, setError] = useState(null);

  const { accountId, domainId } = useParams();
  const fetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}/google/suggest-geo-targets`;

  useEffect(() => {
    if (!selected || selected.length === 0) {
      return;
    }
    fetch(fetchUrl, {
      method: 'POST',
      body: JSON.stringify({
        locations: { geoTargets: { geoTargetConstants: selected.map((code) => `geoTargetConstants/${code}`) } },
      }),
    })
      .then(({ options }) => {
        const stringified = options.map((o) => ({ ...o, value: String(o.value) }));
        setLocationOptions((prev) => {
          const existingValues = new Set(prev.map((o) => o.value));
          const newOptions = stringified.filter((o) => !existingValues.has(o.value));
          return [...prev, ...newOptions];
        });
        setSelectedToShow(stringified.map((o) => o.value));
      })
      .catch(setError);
  }, [fetch, fetchUrl, selected]);

  const handleInputChange = useCallback(
    (input) => {
      if (input.trim()) {
        debouncedLocations(fetch, fetchUrl, input)
          .then(({ options }) => {
            const stringified = options.map((o) => ({ ...o, value: String(o.value) }));
            setLocationOptions((prev) => {
              const existingValues = new Set(prev.map((o) => o.value));
              const newOptions = stringified.filter((o) => !existingValues.has(o.value));
              return [...prev, ...newOptions];
            });
          })
          .catch(setError);
      }
    },
    [fetch, fetchUrl]
  );

  if (error) {
    return <ErrorMessage message={error?.message} />;
  }

  return (
    <MultiSelect
      searchable
      data={locationOptions}
      onChange={(locations) => {
        handleSelect(locations);
        setSelectedToShow(locations);
      }}
      onSearchChange={handleInputChange}
      value={selectedToShow}
      placeholder="Start typing and enter a location..."
    />
  );
};

export default GoogleLocationSelector;
